import React from "react";
import { useI18n } from "../i18n";

export function Affiliate() {
  const { t } = useI18n();

  return (
    <section className="py-20 px-6 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0a0a12 0%, #1a1028 50%, #0a0a12 100%)" }}>
      {/* Animated grid background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: "linear-gradient(#a078d0 1px, transparent 1px), linear-gradient(90deg, #a078d0 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }} />

      {/* Floating orbs */}
      <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-[#a078d0]/5 blur-[80px] onyx-float" />
      <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full bg-[#7b5ea7]/8 blur-[60px] onyx-float" style={{ animationDelay: "1.5s" }} />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div>
            <div className="inline-block px-4 py-1.5 mb-6 rounded-full bg-[#a078d0]/10 border border-[#7b5ea7]/30">
              <span className="text-sm text-[#c0a0e0] font-medium">
                {t("affiliate.badge")}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
              {t("affiliate.title")}
            </h2>

            <p className="text-lg text-[#8a7a9a] mb-8 leading-relaxed">
              {t("affiliate.description")}
            </p>

            <div className="space-y-4 mb-8">
              {(["affiliate.perk1", "affiliate.perk2", "affiliate.perk3", "affiliate.perk4"] as const).map((key) => (
                <div key={key} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-[#a078d0]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3.5 h-3.5 text-[#c0a0e0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <span className="text-[#d0c0e8]">{t(key)}</span>
                </div>
              ))}
            </div>

            <a
              href="/affiliate"
              className="inline-block px-8 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-semibold rounded-lg transition-colors onyx-shimmer"
            >
              {t("affiliate.cta")}
            </a>
          </div>

          {/* Right — Earnings visualization */}
          <div className="relative">
            <div className="rounded-2xl border border-[#2a2040] p-8 onyx-glow" style={{ background: "linear-gradient(145deg, #14121e, #1a1530)" }}>
              {/* Fake dashboard */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-semibold">{t("affiliate.dashboard")}</h3>
                <span className="text-xs text-[#7a6a9a]">{t("affiliate.thisMonth")}</span>
              </div>

              {/* Revenue cards */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="rounded-lg p-4 bg-[#0a0a12]/60 border border-[#2a2040]">
                  <p className="text-xs text-[#7a6a9a] mb-1">{t("affiliate.referrals")}</p>
                  <p className="text-2xl font-bold onyx-gradient-text onyx-title">47</p>
                </div>
                <div className="rounded-lg p-4 bg-[#0a0a12]/60 border border-[#2a2040]">
                  <p className="text-xs text-[#7a6a9a] mb-1">{t("affiliate.earnings")}</p>
                  <p className="text-2xl font-bold onyx-gradient-text onyx-title">$940</p>
                </div>
              </div>

              {/* Fake bar chart */}
              <div className="flex items-end gap-2 h-24">
                {[35, 55, 45, 70, 60, 85, 75, 90, 80, 95, 88, 100].map((h, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t-sm transition-all"
                    style={{
                      height: `${h}%`,
                      background: `linear-gradient(180deg, #a078d0 0%, #7b5ea7 100%)`,
                      opacity: 0.3 + (h / 100) * 0.7,
                      animationDelay: `${i * 0.1}s`,
                    }}
                  />
                ))}
              </div>
              <div className="flex justify-between mt-2">
                <span className="text-[10px] text-[#7a6a9a]">Jan</span>
                <span className="text-[10px] text-[#7a6a9a]">Dec</span>
              </div>
            </div>

            {/* Floating notification */}
            <div className="absolute -top-4 -right-4 rounded-xl p-3 border border-[#a078d0]/30 shadow-lg onyx-float" style={{ background: "linear-gradient(145deg, #1a1530, #201540)", animationDelay: "0.5s" }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#a078d0]/20 flex items-center justify-center">
                  <svg className="w-4 h-4 text-[#c0a0e0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] text-[#c0a0e0] font-medium">+$20 commission</p>
                  <p className="text-[9px] text-[#7a6a9a]">New referral converted!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
